import React, { useState } from "react";
import {
  ColumnType,
  AggregationType,
  FilterOperator,
  aggregationTypeFromJSON,
  filterOperatorFromJSON,
} from "build-ts/enums";
import { ColumnInfo, FilterProto, Header, ObservationColumnProto } from "build-ts/metadata";
import { getColumnValues } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import Observation from "components/configuration/Observation";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";

interface ObservationContainerProps {
  type: "observation" | "filter";
  modelName?: string | undefined;
  // Shared props
  columns: ColumnInfo[];
  columnNames?: string[];

  // State props
  selectedObservationColumn: Header | null;
  selectedFilterColumn: ColumnInfo | null;
  selectedAggregation?: AggregationType;
  selectedOperator?: FilterOperator;
  inputValue: string;
  selectedColumnValues: string[];
  selectedFilterColumns: FilterProto[];
  selectedObservationColumns: ObservationColumnProto[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  isLoading: boolean;
  compact?: boolean;

  // Setter props
  setColumnNames: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedObservationColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  setSelectedAggregation: React.Dispatch<React.SetStateAction<AggregationType | undefined>>;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  setSelectedObservationColumns: React.Dispatch<React.SetStateAction<ObservationColumnProto[]>>;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ObservationContainer: React.FC<ObservationContainerProps> = ({
  type,
  modelName,
  columns,
  columnNames,
  selectedObservationColumn,
  selectedFilterColumn,
  selectedAggregation,
  selectedOperator,
  inputValue,
  selectedColumnValues,
  selectedFilterColumns,
  selectedObservationColumns,
  setSelectedObservationColumn,
  setColumnNames,
  setSelectedFilterColumn,
  setSelectedColumnValues,
  setSelectedAggregation,
  setSelectedOperator,
  setInputValue,
  setSelectedFilterColumns,
  setSelectedObservationColumns,
  dateFilter,
  setDateFilterCategory,
  dateFilterCategory,
  setDateFilter,
  isLoading,
  setIsLoading,
  compact,
}) => {
  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const handleObservationColumnChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const columnId = event.target.value;
    const column = columns && columns.find((c) => c.header?.id === columnId);
    setSelectedObservationColumn(column?.header || null);
  };

  const handleFilterColumnChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const columnId = event.target.value;
    const column = columns && columns.find((c) => c.header?.id === columnId);
    if (column?.column_type === ColumnType.ATTRIBUTE) {
      try {
        setFilterLoading(true);
        const columnValues = await getColumnValues(columnId);
        setColumnNames(columnValues);
      }
      catch (error) {
        showToast("Error fetching column values.", "", "error");
      }
      finally {
        setFilterLoading(false);
      }
    }
    setSelectedFilterColumn(column || null);
    setSelectedColumnValues([]);
  };

  const handleAggregationChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAggregation(aggregationTypeFromJSON(event.target.value));
  };

  const handleOperatorChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedOperator(filterOperatorFromJSON(event.target.value));
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setSelectedColumnValues([]);
  };

  const handleAddObservationColumn = () => {
    if (selectedObservationColumn && selectedAggregation) {
      const newObservationColumn: ObservationColumnProto = {
        logical_column_header: selectedObservationColumn,
        aggregation: selectedAggregation,
      };
      setSelectedObservationColumns((prev) => [...prev, newObservationColumn]);
      setSelectedObservationColumn(null);
      setSelectedAggregation(undefined);
    } else {
      showToast("Please select all fields.", "", "error");
    }
  };
  const handleAddFilterColumn = () => {
    if (selectedFilterColumn && selectedOperator && (inputValue || selectedColumnValues)) {
      const values = inputValue ? inputValue.split(",").map((item) => item.trim()) : selectedColumnValues;
      const newObservationColumn = FilterProto.create({
        logical_column_header: selectedFilterColumn.header!,
        operator: selectedOperator,
        values: values,
        date_filter: dateFilter
      });
      setSelectedFilterColumns((prev = []) => [...prev, newObservationColumn]);
      setSelectedFilterColumn(null);
      setSelectedOperator(undefined);
      setInputValue("");
      setSelectedColumnValues([]);
    } else {
      showToast("Please select all fields.", "", "error");
    }
  };

  //function to handle select multiple values
  const handleToggle = (value: string) => {
    const currentIndex = selectedColumnValues.indexOf(value);
    let newSelected = [...selectedColumnValues];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedColumnValues(newSelected);
  };

  const handleDeleteObservationColumn = (indexToDelete: number) => {
    setSelectedObservationColumns((prevColumns) =>
      prevColumns.filter((_, index) => index !== indexToDelete)
    );
  };

  const handleDeleteFilterColumn = (indexToDelete: number) => {
    setSelectedFilterColumns((prevColumns) =>
      prevColumns.filter((_, index) => index !== indexToDelete)
    );
  };
  return (
    <Observation
      type={type}
      modelName={modelName}
      columns={columns}
      columnNames={columnNames}
      selectedObservationColumn={selectedObservationColumn}
      selectedFilterColumn={selectedFilterColumn}
      selectedAggregation={selectedAggregation}
      selectedOperator={selectedOperator}
      inputValue={inputValue}
      selectedColumnValues={selectedColumnValues}
      selectedFilterColumns={selectedFilterColumns}
      selectedObservationColumns={selectedObservationColumns}
      handleObservationColumnChange={handleObservationColumnChange}
      handleFilterColumnChange={handleFilterColumnChange}
      handleAggregationChange={handleAggregationChange}
      handleOperatorChange={handleOperatorChange}
      handleValueChange={handleValueChange}
      handleAddObservationColumn={handleAddObservationColumn}
      handleAddFilterColumn={handleAddFilterColumn}
      handleToggle={handleToggle}
      handleDeleteObservationColumn={handleDeleteObservationColumn}
      handleDeleteFilterColumn={handleDeleteFilterColumn}
      setDateFilter={setDateFilter}
      setDateFilterCategory={setDateFilterCategory}
      dateFilter={dateFilter}
      dateFilterCategory={dateFilterCategory}
      isLoading={filterLoading}
      compact={compact}
    />
  );
};

export default ObservationContainer;